<template>
  <div>
    <app-cells position="between">
      <h1 class="title">Restaurant Receipts</h1>
      <app-button
        v-if="is_general_manager"
        @click.native="onShowCountModal"
        theme="export"
      >
        <img src="@/assets/img/export-icon.svg" alt="Export">
        <span>Export Waiter Tips</span>
      </app-button>
    </app-cells>
    <app-tabs :tabs="tabs_to_show" />
    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="400"
      name="guest_count"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('guest_count')"></button>
        <div class="popup__title">Export Waiter Tips</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-field label="Property" required :marginBottom="20">
              <app-select
                v-model="form.property"
                :options="property_options"
                :clearable="false"
                placeholder="Property"
                :error="$v.form.property.$error"
              />
              <template #error>
                <div v-if="$v.form.property.$dirty && !$v.form.property.required">Required field</div>
              </template>
            </app-form-field>
            <app-form-field label="Period" required :marginBottom="20">
              <div class="date-picker-wrapper">
                <VueCtkDateTimePicker
                  id="date_picker_balance"
                  class="custom-date-time-picker custom-date-time-picker--range"
                  v-model="form.dates"
                  :custom-shortcuts="custom_shortcuts"
                  shortcut="this_month"
                  format="YYYY-MM-DD"
                  formatted="ll"
                  label="Select dates"
                  color="#77b017"
                  button-color="#77b017"
                  overlay
                  no-header
                  no-label
                  locale="en"
                  range
                  noClearButton
                  :class="{'date-time-picker--error': $v.form.dates.start.$error || $v.form.dates.end.$error}"
                />
              </div>
              <template #error>
                <div v-if="$v.form.dates.$dirty && (!$v.form.dates.start.required || !$v.form.dates.end.required)">Required field</div>
              </template>
            </app-form-field>
            <app-cells :indent="false" position="center">
              <app-button ref="submit" type="submit">Get</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import AppTabs from '@/components/AppTabs'
import { getHotelManagementExportWaiterTip, getHotelManagementPropertiesV2 } from '@/http'
import { required } from 'vuelidate/lib/validators'
import { format, parseISO } from 'date-fns'
import moment from 'moment/moment'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'

export default {
  name: 'HotelManagementRestaurantReceiptsLayout',
  components: {AppTabs, VueCtkDateTimePicker },
  data() {
    return {
      tabs: [
        { name: 'hotel-management-restaurant-receipts-guests', label: 'Guests', roles: ['general_manager', 'hotel_reception'] },
        { name: 'hotel-management-restaurant-receipts-management', label: 'Management', roles: ['general_manager'] },
      ],
      tabs_to_show: [],
      form: {
        dates: {},
      },
      property_options: [],
      custom_shortcuts: [
        {
          key: 'this_month',
          label: 'This month',
          value: () => {
            return {
              start: moment().startOf('month'),
              end: moment().endOf('month')
            }
          }
        },
        {
          key: 'next_month',
          label: 'Next month',
          value: () => {
            return {
              start: moment().startOf('month').add(1, 'months'),
              end: moment().endOf('month').add(1, 'months')
            }
          }
        },
      ],
    }
  },
  validations: {
    form: {
      property: {required},
      dates: {
        start: {required},
        end: {required},
      },
    },
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    getHotelManagementPropertiesV2().then(response => { this.property_options = response.data })
    this.tabsToShow()
  },
  methods: {
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      getHotelManagementExportWaiterTip({property: this.form.property.code, from_date: this.form.dates.start, to_date: this.form.dates.end})
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `Waiter Tips ${this.form.property.name} (${this.dateFormat(this.form.dates.start)} - ${this.dateFormat(this.form.dates.end)}).xlsx`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.$modal.hide('guest_count')
        })
        .catch(error => { this.$refs.submit.preload = false })
    },
    onShowCountModal() {
      this.form = {
        dates: {}
      }
      this.$v.form.$reset()
      this.$modal.show('guest_count')
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    tabsToShow() {
      const tabs = []
      this.tabs.forEach(item => {
        if (!item.roles.length) {
          tabs.push(item)
          return
        }
        item.roles.forEach(role => {
          if (this.$store.state.userGroups.some(groupRole => groupRole.code === role)) tabs.push(item)
        })
      })
      this.tabs_to_show = tabs
    },
  }
}
</script>
